import { Icons } from '@/components/icons';
import BookingAboutToExpireDialog from '@/components/purchase/checkout/expiration-timer/BookingAboutToExpireDialog';
import BookingExpiredDialog from '@/components/purchase/checkout/expiration-timer/BookingExpiredDialog';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { intervalToDuration, isAfter } from 'date-fns';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ExpiryData {
  timeTillExpiry: string;
  showBookingExpiredDialog: boolean;
  showBookingAboutToExpireDialog: boolean;
}

let expiryInterval: ReturnType<typeof setInterval>;

const BookingExpirationTimer: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const booking = useSelector(purchaseFlowBookingSelector);
  const { setCurrentSearchFormValues } = useSearchWidget();
  const [
    bookingAboutToExpireDialogDismissed,
    setBookingAboutToExpireDialogDismissed,
  ] = useState(false);
  const [bookingAboutToExpireDialogOpen, setBookingAboutToExpireDialogOpen] =
    useState(false);
  const [bookingExpiredDialogOpen, setBookingExpiredDialogOpen] =
    useState(false);

  const parsedConfirmableUntil = useMemo(
    () =>
      booking?.confirmableUntil
        ? new Date(booking.confirmableUntil)
        : new Date(),
    [booking?.confirmableUntil]
  );

  const [expiryData, setExpiryData] = useState<ExpiryData>(
    getExpiryData(new Date(), parsedConfirmableUntil)
  );

  const updateExpiryCount = useCallback(() => {
    const expiryData = getExpiryData(new Date(), parsedConfirmableUntil);

    if (
      !bookingAboutToExpireDialogOpen &&
      !bookingAboutToExpireDialogDismissed &&
      expiryData.showBookingAboutToExpireDialog
    )
      setBookingAboutToExpireDialogOpen(true);

    if (expiryData.showBookingExpiredDialog) {
      setBookingAboutToExpireDialogOpen(false);
      setBookingAboutToExpireDialogDismissed(true);
      setBookingExpiredDialogOpen(true);
      clearInterval(expiryInterval);
    }

    setExpiryData(expiryData);
  }, [
    bookingAboutToExpireDialogOpen,
    bookingAboutToExpireDialogDismissed,
    parsedConfirmableUntil,
  ]);

  useEffect(() => {
    expiryInterval = setInterval(updateExpiryCount, 1000);

    return () => clearInterval(expiryInterval);
  }, [updateExpiryCount]);

  const handleBookingExpiredModalCancel = useCallback(() => {
    dispatch(resetPurchase());
    setCurrentSearchFormValues(undefined);
    persistor.purge();
    navigate('/purchase');
  }, [dispatch, navigate, setCurrentSearchFormValues]);

  return (
    <>
      <div className="flex h-8 w-20 items-center justify-start gap-1 rounded-md bg-white p-2 text-primary">
        <Icons.shoppingCart />
        {expiryData.timeTillExpiry}
      </div>
      <BookingAboutToExpireDialog
        open={bookingAboutToExpireDialogOpen}
        timeTillExpiry={expiryData.timeTillExpiry}
        setOpen={setBookingAboutToExpireDialogOpen}
        onCancel={() => setBookingAboutToExpireDialogDismissed(true)}
      />
      <BookingExpiredDialog
        open={bookingExpiredDialogOpen}
        setOpen={setBookingExpiredDialogOpen}
        onCancel={handleBookingExpiredModalCancel}
      />
    </>
  );
};

export default BookingExpirationTimer;

const getExpiryData = (start: Date, end: Date): ExpiryData => {
  const isExpired = isAfter(start, end);
  const durationTillExpiry = intervalToDuration({
    start,
    end: isExpired ? start : end,
  });

  const timeTillExpiry =
    durationTillExpiry &&
    [durationTillExpiry.minutes ?? 0, durationTillExpiry.seconds ?? 0]
      .map((num) => String(num).padStart(2, '0'))
      .join(':');

  const showBookingAboutToExpireDialog = (durationTillExpiry.minutes ?? 0) < 5;

  return {
    timeTillExpiry,
    showBookingExpiredDialog: isExpired,
    showBookingAboutToExpireDialog,
  };
};
